import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { Trans, useTranslation } from "react-i18next";
import { useReactiveVar } from "@apollo/client";
import Countdown from "react-countdown";
import { GetStaticProps } from "next";
import Head from "next/head";
import moment from "moment";
import clsx from "clsx";

import { LeaderboardType } from "@/graphql/types";
import getCountdownRenderer from "@/components/reusable/CountdownRenderer";
import HeadUrlMeta from "@/components/HeadUrlMeta";
import { geti18nNamespaces } from "@/utils/i18n";
import LeaderboardCard from "@/components/LeaderboardCard";
import Page, { PageBody, PageTitle } from "@/components/ui/Page";
import RegisterCTAs from "@/components/RegisterCTAs";
import LoggedOutOnly from "@/components/LoggedOutOnly";
import { hasAccessTokenVar } from "@/apollo/links/auth";

export default function LeaderboardsPage() {
  const { t } = useTranslation(["common", "leaderboards"]);
  const isLogged = useReactiveVar(hasAccessTokenVar);

  return (
    <>
      <Head>
        <title>{t("leaderboards:leaderboards.metadata.title")} - Pianity</title>
        <meta name="description" content={t("leaderboards:leaderboards.metadata.description")} />
      </Head>
      <HeadUrlMeta
        path="/leaderboards"
        title={t("leaderboards:leaderboards.metadata.title")}
        description={t("leaderboards:leaderboards.metadata.description")}
      />

      <Page>
        <div
          className={clsx(
            "container flex flex-col sm:flex-row sm:items-end",
            "sm:justify-between text-white mb-15 lg:mb-20"
          )}
        >
          <div className="flex flex-col">
            <PageTitle>{t("leaderboards:leaderboards.title")}</PageTitle>
            <p
              className={clsx(
                "z-10 text-base leading-1.2 sm:text-lg sm:leading-1.2",
                "lg:text-22 lg:leading-1.2 text-white",
                "mt-3 lg:mt-5 lg:max-w-[38ch]"
              )}
            >
              <Trans i18nKey="leaderboards.description" ns="leaderboards">
                text
                <a
                  className={clsx(
                    "text-xs leading-1.2 lg:text-sm lg:leading-1.2",
                    "font-medium uppercase tracking-wider text-purple-3 hover:underline"
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://help.pianity.com/hc/en-us/articles/5138628214301-What-are-the-Leaderboards-"
                />
              </Trans>
            </p>
          </div>

          <div
            className={clsx(
              "w-max h-6 lg:h-8 px-1.5 lg:px-2 flex items-center rounded bg-purple-3/30",
              "text-white text-sm leading-1.2 lg:text-lg lg:leading-1.25 font-medium whitespace-pre"
            )}
          >
            <Trans i18nKey="leaderboards.reset" ns="leaderboards">
              text
              <Countdown
                date={moment().utc().add(1, "weeks").startOf("isoWeek").toISOString()}
                renderer={getCountdownRenderer(undefined, { displayRed: () => false })}
              />
            </Trans>
          </div>
        </div>

        <PageBody roundedBottom={!isLogged}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-10">
            <LeaderboardCard
              key={LeaderboardType.TrackReports}
              leaderboard={LeaderboardType.TrackReports}
              title={t("common:common.leaderboards.moderators")}
              subtitle={t("leaderboards:leaderboards.moderators.description")}
            />
            <LeaderboardCard
              key={LeaderboardType.NftNumber}
              leaderboard={LeaderboardType.NftNumber}
              title={t("common:common.leaderboards.collectors_primary")}
              subtitle={t("leaderboards:leaderboards.collectors.description")}
            />
            <LeaderboardCard
              key={LeaderboardType.NftNumberSecondary}
              leaderboard={LeaderboardType.NftNumberSecondary}
              title={t("common:common.leaderboards.collectors_secondary")}
              subtitle={t("leaderboards:leaderboards.collectors.secondary.description")}
            />
          </div>
        </PageBody>

        <LoggedOutOnly>
          <RegisterCTAs />
        </LoggedOutOnly>
      </Page>
    </>
  );
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale ?? "en", geti18nNamespaces(["leaderboards"]))),
    },
  };
};
